import * as React from 'react';
import {
  Grid,
  GridColumn,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid';
import { Pager, PagerProps } from '@progress/kendo-react-data-tools';
import { useDispatch, useSelector } from 'react-redux';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { Dispatch } from 'redux';
import {
  IntlProvider,
  loadMessages,
  LocalizationProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';

import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import {
  ReportDataGrid,
  ReportDataRow,
  ReportQueryResult,
} from '../../Interfaces/Reports/ReportQueryResult';
import {
  Dialog,
  DialogActionsBar,
  ActionsLayout,
} from '@progress/kendo-react-dialogs';
import { GetString } from '../StringResourceManager';
import { GridLayout, GridLayoutItem } from '@progress/kendo-react-layout';
import esMessages from '../../utils/gridMessageKey.json';
import { ApplicationState } from '../../store';
loadMessages(esMessages, 'fr-CA');
import {
  fetchGridPageByNode,
  fetchGridPageByStore,
} from '../../store/Reports/ReportsActions';
import { ReportFilter } from '../../Interfaces/Reports/ReportFilter';
import { ReportQueryGridPageByStore } from '../../Interfaces/Reports/ReportQueryInfoByStore';
import { ReportQueryGridPageByNode } from '../../Interfaces/Reports/ReportQueryInfoByNode';
import {
  CenteredCustomCell,
  CenteredHeaderCustomCell,
} from '../ColumnMenus/ColumnMenu';
import '../WrappingGridCustomHeaderTitle.css';
import './ReportsGridKendoCustomStyles.css';
import { LoadingPanel } from '../LoadingPanel';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      fontSize: '1.1rem',
      display: 'inline',
      fontWeight: 'bold',
    },
    spaceBetween: {
      padding: 10,
    },
    modalItemImage: {
      maxWidth: '60vw',
      maxHeight: '55vh',
    },
    gridLayout: {
      fontSize: '15px',
      padding: '10px',
      fontWeight: 'bold',
    },
    gridLabel: {
      font: '14px',
      border: '1px solid #ccc',
      textAlign: 'end',
      paddingLeft: '5px',
      paddingRight: '5px',
    },
    gridValue: {
      font: '14px',
      border: '1px solid #ccc',
      textAlign: 'start',
      color: 'darkblue',
      paddingLeft: '5px',
    },
    dialogContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      maxWidth: '80vw',
    },
  }),
);

const CustomPager: React.FC<PagerProps> = props => {
  const itemsThisPage = Math.min(
    props.take,
    Math.max(props.total - props.skip, 0),
  );
  const SHOWING_ITEMS = GetString('SHOWING_ITEMS');

  return (
    <div
      className="k-pager-wrap k-grid-pager"
      style={{ display: 'flex', alignItems: 'center', gap: 12 }}
    >
      <Pager {...props} />
      <div className="k-pager-info k-label" style={{ marginLeft: 'auto' }}>
        {SHOWING_ITEMS}
        {itemsThisPage}
      </div>
    </div>
  );
};

interface Props {
  grid: ReportDataGrid;
}

const ReportsGrid: React.FC<Props> = ({ grid }): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const DEFAULT_TOTAL_COUNT = 100000;

  const [itemImageFile, setItemImageFile] = React.useState<string>();

  const [rowData, setRowData] = React.useState<any>();

  const [isImageFileModalVisible, setImageFileModalVisible] =
    React.useState<boolean>(false);

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const reportFilter: ReportFilter = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.reportFilter,
  );

  const [layout, setLayout] = React.useState<ActionsLayout>('center');

  const [gridData, setGridData] = React.useState<ReportDataRow[]>();

  const loadingReport: boolean = useSelector(
    (state: ApplicationState) => state.reportsReducer.isFetchingReportData,
  );
  const [loadingPage, setLoadingPage] = React.useState<boolean>(false);

  const [skip, setSkip] = React.useState<number | undefined>(
    grid.Pageable ? (grid.Skip ? grid.Skip : 0) : undefined,
  );

  const [take, setTake] = React.useState<number | undefined>(
    grid.Pageable ? (grid.Take ? grid.Take : grid.DefaultTake) : undefined,
  );

  const [total, setTotal] = React.useState<number>(
    grid.Pageable && grid.TotalCount ? grid.TotalCount : grid.Rows.length,
  );

  const CLOSE_STR = GetString('CLOSE_STR');

  const linkCell = (props: GridCellProps) => {
    let field: string | undefined = props.field;
    let propsArray: string[] = field?.split('.') ?? [];
    let propName =
      propsArray && propsArray.length === 2 ? propsArray[1] : 'Image';
    let srcUrl = props.dataItem.Data[propName];

    return (
      <td
        style={{
          padding: 5,
        }}
      >
        <img
          width={100}
          src={srcUrl}
          onClick={() => handleLinkClicked(true, srcUrl, props.dataItem.Data)}
        />
      </td>
    );
  };

  const handleLinkClicked = (
    modalVisible: boolean,
    itemImageFile: string,
    dataItem: any,
  ) => {
    setImageFileModalVisible(modalVisible);
    setItemImageFile(itemImageFile);
    setRowData(dataItem);
  };

  const getFileNameFromUrl = (value: string) => {
    if (!value) return '';

    try {
      const url = new URL(value);
      return url.pathname.replace(/^\/+/, '');
    } catch {
      return value;
    }
  };

  const getReportDialogGridLayout = () => {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          maxHeight: '30vh',
          border: '1px solid #000000',
          minWidth: '35vw',
          maxWidth: '55vw',
        }}
      >
        <GridLayout
          cols={[{ width: 'auto' }, { width: 'auto' }]}
          rows={grid.Columns.map(() => ({ height: 'auto' }))}
          className={classes.gridLayout}
          align={{ horizontal: 'stretch' }}
        >
          {grid?.Columns?.length > 0
            ? grid.Columns.map(c => {
                return (
                  <>
                    <GridLayoutItem
                      col={1}
                      colSpan={1}
                      className={classes.gridLabel}
                    >
                      {c.HeaderText}
                    </GridLayoutItem>
                    <GridLayoutItem
                      col={2}
                      colSpan={1}
                      className={classes.gridValue}
                    >
                      {getFileNameFromUrl(
                        rowData[c.PropertyName.replace('Data.', '')],
                      )}
                    </GridLayoutItem>
                  </>
                );
              })
            : null}
        </GridLayout>
      </div>
    );
  };

  React.useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const fetchPageData = (skip: number, take: number) => {
    if (
      reportFilter.SiteNumbers.length > 1 ||
      reportFilter.LaneNumbers.length === 0
    ) {
      let query: ReportQueryGridPageByStore = {
        ReportID: reportData.ReportID,
        SiteNumbers: reportFilter.SiteNumbers,
        StartDateTime: reportFilter.StartDateTime,
        EndDateTime: reportFilter.EndDateTime,
        Language: selectedLanguage,
        Skip: skip,
        Take: take,
        GridID: grid.Id,
      };
      fetchGridPageByStore(dispatch, query);
    } else {
      let query: ReportQueryGridPageByNode = {
        ReportID: reportData.ReportID,
        SiteNumber: reportFilter.SiteNumbers[0],
        LaneNumbers: reportFilter.LaneNumbers,
        StartDateTime: reportFilter.StartDateTime,
        EndDateTime: reportFilter.EndDateTime,
        Language: selectedLanguage,
        Skip: skip,
        Take: take,
        GridID: grid.Id,
      };
      fetchGridPageByNode(dispatch, query);
    }
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setLoadingPage(true);
    setSkip(event.page.skip);
    setTake(event.page.take);
    fetchPageData(event.page.skip, event.page.take);
  };

  const reportData: ReportQueryResult = useSelector(
    (state: ApplicationState) => state.reportsReducer.reportData,
  );

  React.useEffect(() => {
    if (grid.Pageable && take !== undefined && skip !== undefined) {
      if (grid.Rows.length < take) {
        const newTotal = skip + grid.Rows.length;
        setTotal(newTotal);
      } else {
        setTotal(grid.TotalCount ? grid.TotalCount : DEFAULT_TOTAL_COUNT);
      }
    }
    setGridData([...grid.Rows]);
    setLoadingPage(false);
  }, [grid.Rows]);

  React.useEffect(() => {
    if (loadingReport && grid.Pageable) {
      setSkip(0);
      setTake(grid.DefaultTake);
    }
  }, [loadingReport]);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <div className={classes.spaceBetween}>
            {grid.Header ? (
              <Typography
                variant="body1"
                className={classes.header}
                gutterBottom
              >
                {grid.Header}
              </Typography>
            ) : null}

            <Grid
              style={{
                minHeight: '100px',
                maxHeight: '600px',
                maxWidth: '79vw',
              }}
              className="wrapping-grid-custom-header"
              data={gridData}
              dataItemKey={grid.KeyProperty}
              reorderable={true}
              resizable={true}
              pageable={
                grid.Pageable
                  ? {
                      buttonCount: 0,
                      pageSizes: [10, 50, 100, 200, 500],
                      info: false,
                      previousNext: true,
                    }
                  : undefined
              }
              onPageChange={handlePageChange}
              total={grid.Pageable ? total : grid.Rows.length}
              defaultTake={
                grid.Pageable
                  ? grid.DefaultTake
                    ? grid.DefaultTake
                    : 100
                  : undefined
              }
              scrollable={'scrollable'}
              skip={skip}
              take={take}
              pager={CustomPager}
              loader={grid.Pageable ? LoadingPanel : undefined}
              showLoader={grid.Pageable ? loadingPage : undefined}
            >
              {grid?.Columns?.length > 0
                ? grid.Columns.map((c, index) => {
                    return (
                      <GridColumn
                        field={c.PropertyName}
                        title={c.HeaderText}
                        key={index}
                        minResizableWidth={60}
                        resizable={true}
                        cells={{
                          data:
                            c.DataType == 'link'
                              ? linkCell
                              : CenteredCustomCell,
                          headerCell: CenteredHeaderCustomCell,
                        }}
                      />
                    );
                  })
                : null}
            </Grid>
          </div>

          {isImageFileModalVisible && (
            <Dialog
              onClose={() => setImageFileModalVisible(false)}
              title={grid.Header}
              style={{
                fontWeight: 'bold',
              }}
            >
              <div className={classes.dialogContainer}>
                <div
                  style={{
                    maxHeight: '50vh',
                    flexShrink: 0,
                    display: 'flex',
                    border: '1px solid #000000',
                    marginBottom: '5px',
                  }}
                >
                  <img
                    src={itemImageFile}
                    className={classes.modalItemImage}
                    style={{
                      objectFit: 'contain',
                    }}
                  />
                </div>
                {getReportDialogGridLayout()}
                <DialogActionsBar layout={layout}>
                  <button
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                    onClick={() => setImageFileModalVisible(false)}
                  >
                    {CLOSE_STR}
                  </button>
                </DialogActionsBar>
              </div>
            </Dialog>
          )}
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default ReportsGrid;
